import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import SystemStatusBanner from '../../components/ui/SystemStatusBanner';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import MetricCard from './components/MetricCard';
import ChartWidget from './components/ChartWidget';
import DataTable from './components/DataTable';
import DateRangeSelector from './components/DateRangeSelector';
import AlertPanel from './components/AlertPanel';
import ExportModal from './components/ExportModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';


const ReportingAnalyticsDashboard = () => {
  const [selectedView, setSelectedView] = useState('overview');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '2026-01-14', end: '2026-01-21' });
  const [loading, setLoading] = useState(true);
  const [keyMetrics, setKeyMetrics] = useState([]);
  const [volumeData, setVolumeData] = useState([]);
  const [carrierDistribution, setCarrierDistribution] = useState([]);
  const [performanceTrend, setPerformanceTrend] = useState([]);
  const [recentShipments, setRecentShipments] = useState([]);
  const [userProductivity, setUserProductivity] = useState([]);
  const [systemAlerts, setSystemAlerts] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch data from Supabase - replace with actual table queries
      // For now, setting empty arrays until tables are created
      setKeyMetrics([]);
      setVolumeData([]);
      setCarrierDistribution([]);
      setPerformanceTrend([]);
      setRecentShipments([]);
      setUserProductivity([]);
      setSystemAlerts([]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const shipmentColumns = [
    { 
      key: 'trackingNumber', 
      label: 'Tracking Number', 
      sortable: true,
      render: (value) => (
        <span className="font-mono text-[13px]">{value}</span>
      )
    },
    { 
      key: 'carrier', 
      label: 'Carrier', 
      sortable: true 
    },
    { 
      key: 'timestamp', 
      label: 'Timestamp', 
      sortable: true 
    },
    { 
      key: 'user', 
      label: 'User', 
      sortable: true 
    },
    { 
      key: 'status', 
      label: 'Status', 
      sortable: true,
      render: (value) => (
        <span className={`
          px-12 py-4 rounded-full font-caption font-semibold text-[11px]
          ${value === 'Validated' ?'bg-success/10 text-success' :'bg-error/10 text-error'
          }
        `}>
          {value}
        </span>
      )
    }
  ];

  const productivityColumns = [
    { key: 'user', label: 'User', sortable: true },
    { key: 'processed', label: 'Processed', sortable: true },
    { key: 'duplicates', label: 'Duplicates', sortable: true },
    { key: 'avgTime', label: 'Avg Time', sortable: true },
    { 
      key: 'accuracy', 
      label: 'Accuracy', 
      sortable: true,
      render: (value) => (
        <span className={`
          font-semibold
          ${parseFloat(value) >= 98 ? 'text-success' : parseFloat(value) >= 95 ? 'text-warning-foreground' : 'text-error'}
        `}>
          {value}
        </span>
      )
    }
  ];

  const viewOptions = [
    { value: 'overview', label: 'Overview Dashboard' },
    { value: 'coordinator', label: 'Coordinator View' },
    { value: 'manager', label: 'Manager View' },
    { value: 'admin', label: 'Admin View' }
  ];

  const handleExport = (options) => {
    console.log('Exporting report with options:', options);
  };

  const handleDateRangeApply = (range) => {
    setDateRange(range);
    console.log('Date range updated:', range);
  };

  return (
    <>
      <Helmet>
        <title>Reporting & Analytics Dashboard - TrackingManager</title>
        <meta name="description" content="Comprehensive analytics and reporting dashboard for shipping operations with real-time metrics, performance tracking, and automated report generation." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <SystemStatusBanner />
        <QuickActionToolbar />

        <main className="pt-[60px]">
          <div className="max-w-screen-2xl mx-auto px-18 md:px-24 py-24 md:py-32">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-18 mb-24 md:mb-32">
              <div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-8">
                  Analytics Dashboard
                </h1>
                <p className="font-body text-sm md:text-base text-muted-foreground">
                  Comprehensive insights and performance metrics for shipping operations
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-12">
                <Select
                  options={viewOptions}
                  value={selectedView}
                  onChange={setSelectedView}
                  placeholder="Select view"
                  className="w-full sm:w-[200px]"
                />
                <Button
                  variant="default"
                  onClick={() => setIsExportModalOpen(true)}
                  iconName="Download"
                  iconPosition="left"
                  className="w-full sm:w-auto"
                >
                  Export Report
                </Button>
              </div>
            </div>

            <DateRangeSelector onApply={handleDateRangeApply} />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-18 md:gap-24 mt-24 md:mt-32">
              {keyMetrics?.map((metric, index) => (
                <MetricCard key={index} {...metric} />
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-18 md:gap-24 mt-24 md:mt-32">
              <ChartWidget
                title="Daily Processing Volume"
                type="bar"
                data={volumeData}
                onExport={() => console.log('Export volume chart')}
                onRefresh={() => console.log('Refresh volume chart')}
              />
              <ChartWidget
                title="Carrier Distribution"
                type="pie"
                data={carrierDistribution}
                onExport={() => console.log('Export carrier chart')}
                onRefresh={() => console.log('Refresh carrier chart')}
              />
            </div>

            <div className="mt-24 md:mt-32">
              <ChartWidget
                title="Monthly Performance Trend"
                type="line"
                data={performanceTrend}
                onExport={() => console.log('Export trend chart')}
                onRefresh={() => console.log('Refresh trend chart')}
                height={350}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-18 md:gap-24 mt-24 md:mt-32">
              <div className="lg:col-span-2">
                <DataTable
                  title="Recent Shipments"
                  columns={shipmentColumns}
                  data={recentShipments}
                  onExport={() => console.log('Export shipments')}
                  pageSize={5}
                />
              </div>
              <div>
                <AlertPanel alerts={systemAlerts} />
              </div>
            </div>

            <div className="mt-24 md:mt-32">
              <DataTable
                title="User Productivity Metrics"
                columns={productivityColumns}
                data={userProductivity}
                onExport={() => console.log('Export productivity')}
                pageSize={5}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-18 md:gap-24 mt-24 md:mt-32">
              <div className="bg-card rounded-lg border border-border p-18 md:p-24">
                <div className="flex items-center gap-12 mb-18">
                  <Icon name="Database" size={20} color="var(--color-primary)" strokeWidth={2} />
                  <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
                    Database Status
                  </h3>
                </div>
                <div className="space-y-12">
                  <div className="flex items-center justify-between">
                    <span className="font-body text-[13px] md:text-[14px] text-muted-foreground">
                      Total Records
                    </span>
                    <span className="font-body font-semibold text-[14px] md:text-[15px] text-foreground">
                      847,293
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-[13px] md:text-[14px] text-muted-foreground">
                      Storage Used
                    </span>
                    <span className="font-body font-semibold text-[14px] md:text-[15px] text-foreground">
                      2.4 GB
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-[13px] md:text-[14px] text-muted-foreground">
                      Last Backup
                    </span>
                    <span className="font-body font-semibold text-[14px] md:text-[15px] text-success">
                      2 hours ago
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-18 md:p-24">
                <div className="flex items-center gap-12 mb-18">
                  <Icon name="Activity" size={20} color="var(--color-primary)" strokeWidth={2} />
                  <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
                    API Performance
                  </h3>
                </div>
                <div className="space-y-12">
                  <div className="flex items-center justify-between">
                    <span className="font-body text-[13px] md:text-[14px] text-muted-foreground">
                      Avg Response
                    </span>
                    <span className="font-body font-semibold text-[14px] md:text-[15px] text-success">
                      1.8s
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-[13px] md:text-[14px] text-muted-foreground">
                      Success Rate
                    </span>
                    <span className="font-body font-semibold text-[14px] md:text-[15px] text-success">
                      99.2%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-[13px] md:text-[14px] text-muted-foreground">
                      Uptime
                    </span>
                    <span className="font-body font-semibold text-[14px] md:text-[15px] text-success">
                      99.9%
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-18 md:p-24">
                <div className="flex items-center gap-12 mb-18">
                  <Icon name="Shield" size={20} color="var(--color-primary)" strokeWidth={2} />
                  <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
                    Security & Compliance
                  </h3>
                </div>
                <div className="space-y-12">
                  <div className="flex items-center justify-between">
                    <span className="font-body text-[13px] md:text-[14px] text-muted-foreground">
                      Audit Logs
                    </span>
                    <span className="font-body font-semibold text-[14px] md:text-[15px] text-success">
                      Active
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-[13px] md:text-[14px] text-muted-foreground">
                      Data Encryption
                    </span>
                    <span className="font-body font-semibold text-[14px] md:text-[15px] text-success">
                      Enabled
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-[13px] md:text-[14px] text-muted-foreground">
                      Compliance
                    </span>
                    <span className="font-body font-semibold text-[14px] md:text-[15px] text-success">
                      100%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <ExportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          onExport={handleExport}
        />
      </div>
    </>
  );
};

export default ReportingAnalyticsDashboard;